import React from "react";
import { Link } from "react-router-dom";   // ✅ ADD THIS LINE
import { SEOHead } from "@/components/SEOHead";
import { useLanguage } from "@/contexts/LanguageContext";
import { BarChart3, Shield, Target, Users } from "lucide-react";

const AboutPage = () => {
  const { t } = useLanguage();

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 text-gray-300">
      <SEOHead
        title={t("aboutPage.title")}
        description="Lotterics is an independent educational platform focused on probability, statistics, and data analysis using lottery data as a learning model."
        path="/about"
      />

      <h1 className="text-3xl font-bold mb-6 text-white">
        {t("aboutPage.title")}
      </h1>

      {/* INTRO — HUMAN, CLEAR, EDUCATIONAL */}
      <div className="mb-10 text-gray-400 leading-relaxed">
        <p className="mb-3">
          I created this project because I know how difficult it can be to find
          yourself looking for clarity in a world full of misleading promises.
        </p>
        <p className="mb-3">
          This is not a promise of winning.  
          It is not a shortcut.  
          It is not a miracle.
        </p>
        <p className="mb-3">
          It is work.  
          Data.  
          Mathematics.  
          And honesty about how challenging the odds truly are.
        </p>
        <p className="italic text-sm text-gray-500">
          That help is education — understanding probability, statistics, and
          randomness, not predicting outcomes.
        </p>
      </div>

      {/* MISSION */}
      <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-white mb-3">
          Our Mission
        </h2>
        <p className="text-gray-300 leading-relaxed">
          Lotterics was created to promote transparency, critical thinking, and
          data literacy through the exploration of lottery statistics.
          Our mission is to provide an educational environment where users can
          examine historical draw data, visualize numerical distributions, and
          better understand probability and randomness across major international
          lotteries.
        </p>
      </div>

      {/* WHAT IT IS / IS NOT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
        <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-5">
          <h3 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            What Lotterics Is
          </h3>
          <ul className="text-sm text-gray-300 space-y-2">
            <li>• An educational and informational platform</li>
            <li>• A statistical analysis and data visualization tool</li>
            <li>• An independent learning resource</li>
            <li>• A space to explore historical lottery data responsibly</li>
          </ul>
        </div>

        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-5">
          <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            What Lotterics Is Not
          </h3>
          <ul className="text-sm text-gray-300 space-y-2">
            <li>• Not a gambling or betting service</li>
            <li>• Not a lottery ticket retailer</li>
            <li>• Not affiliated with lottery operators</li>
            <li>• Not a system that guarantees outcomes</li>
          </ul>
        </div>
      </div>

      {/* OFFERINGS */}
      <h2 className="text-xl font-semibold mb-3 text-white">
        What We Provide
      </h2>
      <ul className="list-disc list-inside mb-8 space-y-1 text-gray-300">
        <li>Advanced lottery statistics and historical analysis</li>
        <li>AI-assisted statistical exploration tools</li>
        <li>Number frequency and distribution visualizations</li>
        <li>Historical draw databases</li>
        <li>Probability indicators for educational use</li>
      </ul>

      {/* ANALYTICAL APPROACH */}
      <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
          <Target className="w-5 h-5 text-amber-400" />
          Our Analytical Approach
        </h2>
        <p className="text-gray-300 leading-relaxed mb-3">
          Lotterics aggregates publicly available lottery draw data and applies
          descriptive statistical analysis to explore historical distributions,
          frequencies, and numerical variance.
        </p>
        <p className="text-gray-300 leading-relaxed">
          <strong>Important:</strong> All analyses are descriptive and educational.
          Lottery draws are independent random events, and historical data does
          not influence future outcomes. No analytical method can predict lottery
          results.
        </p>
      </div>

      {/* RESPONSIBLE USE */}
      <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-white mb-3">
          Responsible Use
        </h2>
        <p className="text-gray-300 leading-relaxed">
          Lotterics is designed for educational purposes only. We do not encourage
          excessive lottery participation or the belief that analytical tools can
          improve winning outcomes.
        </p>
        <p className="text-gray-300 leading-relaxed mt-2">
          If lottery participation causes stress, financial difficulty, or
          emotional harm, we strongly encourage seeking support from professional
          responsible gambling organizations.
        </p>
      </div>

      {/* INDEPENDENCE */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-6 mb-10">
        <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Independence & Transparency
        </h2>
        <p className="text-gray-300 leading-relaxed mb-3">
          Lotterics operates independently and has no commercial relationships
          with lottery operators, gaming commissions, or gambling organizations.
          We do not sell tickets, receive commissions, or promote gambling.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The platform is supported through advertising, allowing us to maintain
          editorial independence and provide unbiased educational content.
        </p>
      </div>

      {/* CONTACT */}
      <h2 className="text-xl font-semibold mb-2 text-white">
        Contact
      </h2>
      <p className="mb-4 text-gray-300">
        For questions, feedback, or educational inquiries, please use our contact
        form. Messages are reviewed by the Lotterics editorial team.
      </p>

<Link
  to="/contact"
  className="inline-block px-5 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition"
>
  Go to Contact Form →
</Link>

      {/* FOOTER NOTE */}
      <div className="mt-12 pt-6 border-t border-gray-700">
        <p className="text-sm text-gray-400 text-center mb-4">
          © 2026 Lotterics — Educational purposes only.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/privacy-policy" className="text-amber-400 hover:text-amber-300 text-sm">Privacy Policy</Link>
          <Link to="/terms" className="text-amber-400 hover:text-amber-300 text-sm">Terms of Service</Link>
          <Link to="/disclaimer" className="text-amber-400 hover:text-amber-300 text-sm">Disclaimer</Link>
          <Link to="/blog" className="text-amber-400 hover:text-amber-300 text-sm">Blog</Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
